// Recovery from a client that is stuck on a build the server no longer has.
//
// The failure this exists for: a service worker that never activated kept
// serving an old index.html, the old shell asked for chunk filenames the deploy
// had replaced, every one of them 404'd, and neither Reload nor "Back to start"
// could escape because both are navigations and the same worker answered both.
// In a browser you close the tab. In a WebView there is nothing to close, so
// users were uninstalling the app.
//
// This tears down the two things that can hold a stale build - the service
// worker registration and the Cache Storage buckets - and then navigates to a
// URL the HTTP cache has not seen.
//
// It deliberately does NOT touch localStorage. Tokens, the persisted redux
// state and the org theme all live there, so leaving it alone means recovery
// keeps the user signed in and is safe to run automatically.

const CHUNK_RELOAD_FLAG = 'deskos:chunk-reload';
const RESET_STAMP = 'deskos:last-reset';

// Two resets inside this window means the reset is not the fix, so stop.
const RESET_COOLDOWN_MS = 60 * 1000;

const readStamp = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (ignored) {
    return null;
  }
};

const writeStamp = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (ignored) {
    // Private mode or a full quota. The reset still runs.
  }
};

const clearStamp = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (ignored) {
    // Nothing to clear.
  }
};

// The Flutter shells own a layer we cannot reach from here: the WebView's own
// HTTP cache, which can be holding an index.html of its own. They clear it and
// leave the navigation to us, so there is only ever one of them in flight.
//
// deskos-app exposes `Flutter`, deskos-hub exposes `FlutterHub`. Neither exists
// in a browser, which is the point.
const notifyNativeShell = () => {
  try {
    window.Flutter?.postMessage?.('hardReset');
  } catch (ignored) {
    // Not in the member app.
  }
  try {
    window.FlutterHub?.postMessage?.(JSON.stringify({ type: 'hardReset' }));
  } catch (ignored) {
    // Not in the admin app.
  }
};

/**
 * Unregister every service worker for this origin and empty Cache Storage.
 * Resolves even when parts of it fail - a partial purge still helps.
 */
export async function purgeAppCaches() {
  const jobs = [];

  notifyNativeShell();

  if ('serviceWorker' in navigator) {
    // Ask the active worker to purge first. It owns caches opened in its own
    // scope, and on some WebViews caches.delete() from the page races with a
    // worker that is still serving.
    try {
      navigator.serviceWorker.controller?.postMessage({ type: 'DESKOS_PURGE_CACHES' });
    } catch (ignored) {
      // Best effort.
    }

    jobs.push(
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
        .catch(() => {})
    );
  }

  if (typeof caches !== 'undefined') {
    jobs.push(
      caches
        .keys()
        .then((names) => Promise.all(names.map((name) => caches.delete(name))))
        .catch(() => {})
    );
  }

  await Promise.all(jobs);
}

/**
 * Purge, then land on a fresh copy of the app.
 *
 * @param {object}  options
 * @param {string}  options.to     where to land. Defaults to the app root.
 * @param {boolean} options.force  skip the cooldown (a button the user pressed).
 * @returns {Promise<boolean>} false when the cooldown blocked it.
 */
export async function hardReset({ to = '/', force = false } = {}) {
  const last = Number(readStamp(RESET_STAMP) || 0);

  if (!force && last && Date.now() - last < RESET_COOLDOWN_MS) {
    // We already did this and we are back here anyway. Whatever is wrong is not
    // a stale cache, so let the caller show its error screen instead of
    // bouncing the user around a reload loop.
    return false;
  }

  writeStamp(RESET_STAMP, String(Date.now()));
  clearStamp(CHUNK_RELOAD_FLAG);

  await purgeAppCaches();

  // A plain reload() can be answered from the HTTP cache or from bfcache, and
  // on a WebView it does not always leave the current document. A replace() to
  // a URL carrying a nonce cannot be served from either.
  const target = new URL(to, window.location.origin);
  target.searchParams.set('fresh', Date.now().toString(36));
  window.location.replace(target.toString());

  return true;
}

/**
 * Drop the `fresh` nonce hardReset() added so it does not stay in the address
 * bar, get bookmarked, or end up in an analytics path. Call once at startup.
 */
export function stripResetNonce() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('fresh')) return;

    url.searchParams.delete('fresh');
    const search = url.searchParams.toString();
    window.history.replaceState({}, '', `${url.pathname}${search ? `?${search}` : ''}${url.hash}`);
  } catch (ignored) {
    // Cosmetic only.
  }
}

/**
 * True when the error is a build that moved under us rather than a real bug:
 * webpack could not fetch a lazily imported chunk.
 */
export function isStaleBuildError(error) {
  const text = `${error?.name || ''} ${error?.message || ''}`;
  return (
    /ChunkLoadError/i.test(text) ||
    /Loading chunk [^\s]+ failed/i.test(text) ||
    /Loading CSS chunk/i.test(text) ||
    /error loading dynamically imported module/i.test(text) ||
    /Importing a module script failed/i.test(text) ||
    /Failed to fetch dynamically imported module/i.test(text) ||
    /'text\/html' is not a valid JavaScript MIME type/i.test(text)
  );
}
